import React, { createContext, useContext, useReducer, useEffect } from 'react';
import AuthService from '../utils/authService';

const AuthContext = createContext();

const initialState = {
  user: null,
  userDepartments: [],
  currentDepartment: null,
  isAuthenticated: false,
  loading: true,
  error: null
};

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'LOGIN_SUCCESS':
      // Ensure user is always an object, even if empty
      const user = action.payload.user || {};
      const departments = action.payload.departments || [];
      
      // Try to restore the previously selected department from localStorage
      const savedDepartmentId = localStorage.getItem('currentDepartmentId');
      let selectedDepartment = null;
      
      if (savedDepartmentId && departments.length > 0) {
        selectedDepartment = departments.find(d => d.id === savedDepartmentId);
      }
      
      // If no saved department or saved department not found, use first available
      if (!selectedDepartment && departments.length > 0) {
        selectedDepartment = departments[0];
      }
      
      // 🔥 CRITICAL: Always update localStorage with the selected department
      if (selectedDepartment) {
        localStorage.setItem('currentDepartmentId', selectedDepartment.id);
        console.log(`🔄 AuthContext: Set current department on login: ${selectedDepartment.name} (${selectedDepartment.id})`);
        console.log(`🔄 AuthContext: localStorage updated with departmentId: ${selectedDepartment.id}`);
      } else {
        // Clear localStorage if no department available
        localStorage.removeItem('currentDepartmentId');
        console.log('🔄 AuthContext: No departments available, cleared localStorage');
      }
      
      return {
        ...state,
        user: user,
        userDepartments: departments,
        currentDepartment: selectedDepartment,
        isAuthenticated: !!user && Object.keys(user).length > 0,
        loading: false,
        error: null
      };
    case 'LOGOUT':
      // 🔥 CRITICAL: Always clear localStorage on logout
      localStorage.removeItem('currentDepartmentId');
      console.log('🔄 AuthContext: Cleared current department on logout');
      console.log('🔄 AuthContext: localStorage cleared');
      return { ...initialState, loading: false };
    case 'SWITCH_DEPARTMENT':
      // 🔥 CRITICAL: Always update localStorage when switching departments
      if (action.payload && action.payload.id) {
        localStorage.setItem('currentDepartmentId', action.payload.id);
        console.log(`🔄 AuthContext: Switched to department: ${action.payload.name} (${action.payload.id})`);
        console.log(`🔄 AuthContext: localStorage updated with new departmentId: ${action.payload.id}`);
      } else {
        // Clear localStorage if switching to null/undefined department
        localStorage.removeItem('currentDepartmentId');
        console.log('🔄 AuthContext: Switched to null department, cleared localStorage');
      }
      return {
        ...state,
        currentDepartment: action.payload
      };
    case 'UPDATE_USER_DEPARTMENTS':
      const updatedDepartments = action.payload;
      
      // Try to maintain the current department if it still exists
      let maintainedDepartment = null;
      const savedDeptId = localStorage.getItem('currentDepartmentId');
      
      if (savedDeptId && updatedDepartments.length > 0) {
        maintainedDepartment = updatedDepartments.find(d => d.id === savedDeptId);
      }
      
      // If saved department no longer exists, try current state department
      if (!maintainedDepartment && state.currentDepartment) {
        maintainedDepartment = updatedDepartments.find(d => d.id === state.currentDepartment.id);
      }
      
      // If still no department, select first available
      if (!maintainedDepartment && updatedDepartments.length > 0) {
        maintainedDepartment = updatedDepartments[0];
      }
      
      // 🔥 CRITICAL: Always sync localStorage with the maintained/new department
      if (maintainedDepartment) {
        localStorage.setItem('currentDepartmentId', maintainedDepartment.id);
        console.log(`🔄 AuthContext: Maintained/updated department: ${maintainedDepartment.name} (${maintainedDepartment.id})`);
        console.log(`🔄 AuthContext: localStorage synced with departmentId: ${maintainedDepartment.id}`);
      } else {
        localStorage.removeItem('currentDepartmentId');
        console.log('🔄 AuthContext: No departments available, cleared localStorage');
      }
      
      return {
        ...state,
        userDepartments: updatedDepartments,
        currentDepartment: maintainedDepartment
      };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // 🔥 NEW: Validate and sync localStorage on app start
  useEffect(() => {
    const validateLocalStorage = () => {
      const savedDeptId = localStorage.getItem('currentDepartmentId');
      const currentDeptId = state.currentDepartment?.id;
      
      if (savedDeptId !== currentDeptId) {
        if (currentDeptId) {
          localStorage.setItem('currentDepartmentId', currentDeptId);
          console.log(`🔄 AuthContext: Synced localStorage with current department: ${currentDeptId}`);
        } else if (savedDeptId) {
          localStorage.removeItem('currentDepartmentId');
          console.log('🔄 AuthContext: Removed stale department from localStorage');
        }
      }
    };

    // Only validate after authentication is complete
    if (!state.loading) {
      validateLocalStorage();
    }
  }, [state.currentDepartment?.id, state.loading]);

  const checkAuthStatus = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const authData = await AuthService.checkAuth();
      
      if (authData && authData.user && Object.keys(authData.user).length > 0) {
        console.log('✅ AuthContext: Auth check successful, user authenticated');
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: authData.user,
            departments: authData.departments || []
          }
        });
      } else {
        console.log('❌ AuthContext: Auth check failed, user not authenticated');
        dispatch({ type: 'LOGOUT' });
      }
    } catch (error) {
      console.error('❌ AuthContext: Auth check failed:', error);
      dispatch({ type: 'LOGOUT' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const login = async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      const authData = await AuthService.login(email, password);
      
      // Validate that we have proper user data
      if (!authData || !authData.user || Object.keys(authData.user).length === 0) {
        throw new Error('Ungültige Anmeldedaten erhalten');
      }
      
      console.log('✅ AuthContext: Login successful');
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: authData.user,
          departments: authData.departments || []
        }
      });
    } catch (error) {
      const errorMessage = error.message || 'Anmeldung fehlgeschlagen';
      console.error('❌ AuthContext: Login failed:', errorMessage);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = async () => {
    try {
      await AuthService.logout();
      console.log('✅ AuthContext: Logout successful');
    } catch (error) {
      console.error('❌ AuthContext: Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  // 🔥 ENHANCED: This function triggers department data loading with explicit notification
  const switchDepartment = (department) => {
    console.log(`🔄 AuthContext: Switching to department: ${department?.name} (${department?.id})`);
    
    // Update the department in AuthContext (which will also update localStorage via reducer)
    dispatch({ type: 'SWITCH_DEPARTMENT', payload: department });
    
    // 🔥 ENHANCED: Emit a custom event to notify ScheduleContext about the department change
    // This ensures that the ScheduleContext useEffect is triggered even if the component
    // doesn't re-render immediately
    if (department) {
      const event = new CustomEvent('departmentChanged', {
        detail: { department }
      });
      window.dispatchEvent(event);
      
      // 🔥 NEW: Double-check localStorage consistency
      setTimeout(() => {
        const storedId = localStorage.getItem('currentDepartmentId');
        if (storedId !== department.id) {
          console.warn(`🚨 AuthContext: localStorage inconsistency detected! Stored: ${storedId}, Expected: ${department.id}`);
          localStorage.setItem('currentDepartmentId', department.id);
          console.log(`🔄 AuthContext: Fixed localStorage inconsistency`);
        }
      }, 100);
    }
  };

  const refreshUserDepartments = async () => {
    try {
      const authData = await AuthService.checkAuth();
      if (authData && authData.departments) {
        dispatch({ type: 'UPDATE_USER_DEPARTMENTS', payload: authData.departments });
      }
    } catch (error) {
      console.error('❌ AuthContext: Failed to refresh user departments:', error);
    }
  };

  // 🔥 NEW: Helper function to get current department ID (with localStorage fallback)
  const getCurrentDepartmentId = () => {
    // First try from state
    if (state.currentDepartment?.id) {
      return state.currentDepartment.id;
    }
    
    // Fallback to localStorage
    const savedId = localStorage.getItem('currentDepartmentId');
    if (savedId) {
      console.log(`🔄 AuthContext: Using department ID from localStorage: ${savedId}`);
      return savedId;
    }
    
    console.warn('🚨 AuthContext: No department ID available in state or localStorage');
    return null;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        switchDepartment,
        refreshUserDepartments,
        checkAuthStatus,
        getCurrentDepartmentId // 🔥 NEW: Expose helper function
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}